import React, { useState, useEffect } from "react";
import { 
  Lock, 
  KeyRound, 
  Mail, 
  Copy, 
  Check, 
  Download, 
  Trash2, 
  UserPlus, 
  Search, 
  Users, 
  ShieldCheck, 
  LogOut,
  AlertCircle,
  FileSpreadsheet
} from "lucide-react";
import { Link } from "react-router-dom";

// Default seed list if nothing in localStorage
const DEFAULT_SUBSCRIBERS = [
  { id: "1", name: "Kern Holm Thomsen", email: "Kongeengen@gmail.com", date: "2026-01-15", source: "Hjemmeside" },
  { id: "2", name: "Christian Toft Madsen", email: "kasserer@kongeengen.dk", date: "2026-02-01", source: "Hjemmeside" },
  { id: "3", name: "Mikael Skrydstrup", email: "mikael@kongstedgaards.dk", date: "2026-02-10", source: "Hjemmeside" },
  { id: "4", name: "Morten Jessen", email: "morten.jessen@gmail.com", date: "2026-03-04", source: "Hjemmeside" },
  { id: "5", name: "Daniel Haurum Kristensen", email: "daniel.haurum@outlook.com", date: "2026-04-12", source: "Hjemmeside" },
  { id: "6", name: "Sofie Amalie Berg", email: "sofie.berg@gmail.com", date: "2026-05-18", source: "Hjemmeside" },
  { id: "7", name: "Lars Henrik Nørgaard", email: "lh.noergaard@privat.dk", date: "2026-06-02", source: "Hjemmeside" }
];

const STORAGE_KEY = "kongeengen_newsletter_subscribers";
const DELETED_KEY = "kongeengen_newsletter_deleted_emails";
const AUTH_KEY = "kongeengen_newsletter_authenticated";
const CORRECT_PASSWORD = "kongeengen123!"; // Password for board members

export function getDeletedEmails() {
  try {
    const raw = localStorage.getItem(DELETED_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (err) {
    return [];
  }
}

export function getStoredSubscribers() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const deleted = getDeletedEmails();
    let list = DEFAULT_SUBSCRIBERS;
    if (raw) {
      list = JSON.parse(raw);
    } else {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(DEFAULT_SUBSCRIBERS));
    }
    return list.filter(s => !deleted.includes(s.email.toLowerCase()));
  } catch (err) {
    console.error("Error reading subscribers from localStorage", err);
    return DEFAULT_SUBSCRIBERS;
  }
}

export async function fetchSubscribers() {
  const deletedEmails = getDeletedEmails();
  try {
    const res = await fetch("https://api.dev.auxo.dk/fn/get-newsletter-subscribers");
    if (res.ok) {
      const data = await res.json();
      const apiList = Array.isArray(data) ? data : (data.subscribers || data.items || data.data || []);
      if (apiList && apiList.length > 0) {
        const normalized = apiList.map((item, idx) => {
          const rawName = item.name || item.navn || item.fullName || item.full_name || item.subscriber_name || item.displayName || item.data?.name || item.data?.navn || "";
          const rawEmail = item.email || item.e_mail || item.mail || item.data?.email || "";
          return {
            id: item.id || item.email || String(idx),
            name: rawName && rawName.trim() !== "" ? rawName.trim() : "Anonym beboer",
            email: rawEmail.trim().toLowerCase(),
            date: item.date || item.created_at || item.createdAt || item.tilmeldt_dato || item.data?.date || new Date().toISOString().split("T")[0],
            source: item.source || "Hjemmeside"
          };
        }).filter(s => s.email && !deletedEmails.includes(s.email.toLowerCase()));

        const local = getStoredSubscribers().filter(s => !deletedEmails.includes(s.email.toLowerCase()));
        
        // Merge API data with local data carefully:
        // 1. Preserve real names from local storage if API item has "Anonym beboer"
        const merged = normalized.map(apiItem => {
          const locMatch = local.find(l => l.email.toLowerCase() === apiItem.email.toLowerCase());
          if (locMatch && locMatch.name && locMatch.name !== "Anonym beboer" && apiItem.name === "Anonym beboer") {
            return { ...apiItem, name: locMatch.name };
          }
          return apiItem;
        });

        // 2. Add local entries that aren't in API list yet
        local.forEach(loc => {
          if (!merged.some(m => m.email.toLowerCase() === loc.email.toLowerCase())) {
            merged.push(loc);
          }
        });

        const finalMerged = merged.filter(s => !deletedEmails.includes(s.email.toLowerCase()));
        localStorage.setItem(STORAGE_KEY, JSON.stringify(finalMerged));
        return finalMerged;
      }
    }
  } catch (err) {
    console.error("Fejl ved hentning fra get-newsletter-subscribers backend funktion:", err);
  }
  return getStoredSubscribers();
}

export function saveSubscriber(email, name = "") {
  try {
    const cleanEmail = email.trim().toLowerCase();
    const cleanName = name.trim();

    // If previously deleted, un-blacklist it when re-added
    const deleted = getDeletedEmails();
    if (deleted.includes(cleanEmail)) {
      const newDeleted = deleted.filter(e => e !== cleanEmail);
      localStorage.setItem(DELETED_KEY, JSON.stringify(newDeleted));
    }

    const current = getStoredSubscribers();

    // Check if email already exists
    const existingIndex = current.findIndex(s => s.email.toLowerCase() === cleanEmail);
    if (existingIndex >= 0) {
      // If entry exists but has no name or "Anonym beboer", and a real name was supplied now, update it!
      if (cleanName && cleanName !== "Anonym beboer") {
        current[existingIndex].name = cleanName;
        localStorage.setItem(STORAGE_KEY, JSON.stringify(current));
        window.dispatchEvent(new Event("newsletter_subscribers_updated"));
      }
      return true;
    }

    const newEntry = {
      id: Date.now().toString(),
      name: cleanName || "Anonym beboer",
      email: cleanEmail,
      date: new Date().toISOString().split("T")[0],
      source: "Hjemmeside"
    };

    const updated = [newEntry, ...current];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    // Dispatch custom event so open tabs update in real time
    window.dispatchEvent(new Event("newsletter_subscribers_updated"));
    return true;
  } catch (err) {
    console.error("Error saving subscriber to localStorage", err);
    return false;
  }
}

export default function NewsletterRecipients() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  const [authError, setAuthError] = useState(false);
  
  const [subscribers, setSubscribers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [copiedFormat, setCopiedFormat] = useState(null); // 'comma', 'semicolon', 'line'
  
  // New entry state
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [addMessage, setAddMessage] = useState(null);

  // Load auth status and subscribers on mount
  useEffect(() => {
    const isAuth = sessionStorage.getItem(AUTH_KEY) === "true";
    if (isAuth) {
      setIsAuthenticated(true);
    }

    const loadSubscribers = async () => {
      const data = await fetchSubscribers();
      setSubscribers(data);
    };

    loadSubscribers();

    const handleUpdate = async () => {
      const data = await fetchSubscribers();
      setSubscribers(data);
    };

    window.addEventListener("newsletter_subscribers_updated", handleUpdate);
    window.addEventListener("storage", handleUpdate);
    return () => {
      window.removeEventListener("newsletter_subscribers_updated", handleUpdate);
      window.removeEventListener("storage", handleUpdate);
    };
  }, []);

  const handleLogin = (e) => {
    e.preventDefault();
    if (passwordInput.trim().toLowerCase() === CORRECT_PASSWORD.toLowerCase()) {
      setIsAuthenticated(true);
      setAuthError(false);
      sessionStorage.setItem(AUTH_KEY, "true");
    } else {
      setAuthError(true);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem(AUTH_KEY);
    setPasswordInput("");
  };

  const handleDelete = (id) => {
    const target = subscribers.find(s => s.id === id);
    const targetEmail = target ? target.email.toLowerCase() : null;

    if (window.confirm("Er du sikker på, at du vil fjerne denne e-mailadresse fra modtagerlisten?")) {
      if (targetEmail) {
        const deleted = getDeletedEmails();
        if (!deleted.includes(targetEmail)) {
          deleted.push(targetEmail);
          localStorage.setItem(DELETED_KEY, JSON.stringify(deleted));
        }
      }

      const updated = subscribers.filter(s => s.id !== id && (targetEmail ? s.email.toLowerCase() !== targetEmail : true));
      setSubscribers(updated);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      window.dispatchEvent(new Event("newsletter_subscribers_updated"));
    }
  };

  const handleAddManual = async (e) => {
    e.preventDefault();
    if (!newEmail || !newEmail.includes("@")) {
      setAddMessage({ type: "error", text: "Indtast venligst en gyldig e-mailadresse." });
      return;
    }

    try {
      await fetch("https://api.dev.auxo.dk/fn/subscribe-newsletter", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: newEmail.trim(),
          name: newName.trim(),
          navn: newName.trim(),
          fullName: newName.trim(),
          subscriber_name: newName.trim(),
          website_url: ""
        })
      });
    } catch (err) {
      console.error("Kunne ikke sende til subscribe-newsletter backend funktionen:", err);
    }

    const success = saveSubscriber(newEmail, newName);
    if (success) {
      const updated = await fetchSubscribers();
      setSubscribers(updated);
      setNewEmail("");
      setNewName("");
      setAddMessage({ type: "success", text: "Modtageren er tilføjet til listen!" });
      setTimeout(() => setAddMessage(null), 3000);
    } else {
      setAddMessage({ type: "error", text: "E-mailadressen findes allerede i listen." });
    }
  };

  const filteredSubscribers = subscribers.filter(s => 
    s.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const copyToClipboard = (text, format) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedFormat(format);
      setTimeout(() => setCopiedFormat(null), 2500);
    }).catch(err => {
      console.error("Kunne ikke kopiere", err);
    });
  };

  const getCommaList = () => subscribers.map(s => s.email).join(", ");
  const getSemicolonList = () => subscribers.map(s => s.email).join("; ");
  const getNewlineList = () => subscribers.map(s => s.email).join("\n");

  const downloadCSV = () => {
    const headers = "Navn,E-mail,Tilmeldt Dato,Kilde\n";
    const rows = subscribers.map(s => `"${s.name}","${s.email}","${s.date}","${s.source}"`).join("\n");
    const csvContent = "data:text/csv;charset=utf-8,\uFEFF" + encodeURIComponent(headers + rows);
    
    const link = document.createElement("a");
    link.setAttribute("href", csvContent);
    link.setAttribute("download", `nyhedsbrev_modtagere_kongeengen_${new Date().toISOString().split("T")[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // --- LOGIN SCREEN ---
  if (!isAuthenticated) {
    return (
      <div className="py-20 bg-forest-950/20 min-h-[75vh] flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-3xl p-8 shadow-2xl border border-forest-100/80 space-y-6">
          <div className="text-center space-y-3">
            <div className="w-16 h-16 bg-forest-100 text-forest-800 rounded-2xl flex items-center justify-center mx-auto shadow-inner">
              <ShieldCheck className="w-8 h-8" />
            </div>
            <h1 className="font-display font-bold text-2xl text-forest-950">
              Bestyrelsesadgang
            </h1>
            <p className="text-sm text-forest-600 leading-relaxed">
              Modtagerlisten for nyhedsbrevet er beskyttet. Indtast adgangskoden for at se og eksportere modtagere.
            </p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-forest-800 mb-1.5 uppercase tracking-wider">
                Adgangskode
              </label>
              <div className="relative">
                <input
                  type="password"
                  value={passwordInput}
                  onChange={(e) => {
                    setPasswordInput(e.target.value);
                    if (authError) setAuthError(false);
                  }}
                  placeholder="Indtast adgangskode..."
                  className="w-full pl-10 pr-4 py-3 rounded-xl bg-forest-50/50 border border-forest-200 text-forest-900 placeholder-forest-400 focus:outline-none focus:ring-2 focus:ring-forest-600 focus:bg-white text-sm transition-all"
                  autoFocus
                />
                <KeyRound className="w-5 h-5 text-forest-400 absolute left-3 top-3.5" />
              </div>
              {authError && (
                <p className="mt-2 text-xs text-rose-600 font-medium flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" /> Forkert adgangskode. Prøv igen.
                </p>
              )}
            </div>

            <button
              type="submit"
              className="w-full py-3.5 px-4 bg-forest-800 hover:bg-forest-900 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2 text-sm cursor-pointer"
            >
              <Lock className="w-4 h-4" />
              Lås op for modtagerliste
            </button>
          </form>
        </div>
      </div>
    );
  }

  // --- AUTHENTICATED DASHBOARD ---
  return (
    <div className="py-12 bg-forest-50/30 min-h-[85vh]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
        
        {/* Navigation Tabs between Newsletter & Events */}
        <div className="bg-white rounded-2xl p-2 border border-forest-100 shadow-sm flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Link
              to="/nyhedsbrev-modtagere"
              className="px-4 py-2 rounded-xl text-sm font-semibold bg-forest-800 text-white shadow-sm flex items-center gap-2"
            >
              <Mail className="w-4 h-4" />
              Nyhedsbrev-modtagere
            </Link>
            <Link
              to="/event-tilmeldinger"
              className="px-4 py-2 rounded-xl text-sm font-semibold text-forest-700 hover:text-forest-950 hover:bg-forest-50 transition-colors flex items-center gap-2"
            >
              <Users className="w-4 h-4 text-forest-500" />
              Event-tilmeldinger & Deltagere
            </Link>
          </div>

          <div className="flex items-center gap-3 pr-2">
            <button
              onClick={handleLogout}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-forest-200 hover:bg-forest-50 text-forest-700 text-xs font-medium transition-colors cursor-pointer"
            >
              <LogOut className="w-3.5 h-3.5" /> Lås og log ud
            </button>
          </div>
        </div>

        {/* Top Header */}
        <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-sm border border-forest-100 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-2">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100/80 text-emerald-800 text-xs font-semibold">
              <ShieldCheck className="w-3.5 h-3.5" /> Internt Bestyrelsesværktøj
            </div>
            <h1 className="font-display font-bold text-2xl sm:text-3xl text-forest-950">
              Modtagerliste for Nyhedsbrev
            </h1>
            <p className="text-sm text-forest-600 max-w-2xl">
              Her kan du administrere alle tilmeldte beboere samt nemt kopiere e-mailadresser til brug i Gmail, Outlook eller andre mailklienter.
            </p>
          </div>

          <div className="flex items-center gap-3 shrink-0">
            <button
              onClick={handleLogout}
              className="px-4 py-2.5 rounded-xl border border-forest-200 bg-white hover:bg-forest-50 text-forest-700 text-xs font-semibold flex items-center gap-2 transition-colors cursor-pointer"
            >
              <LogOut className="w-4 h-4" />
              Lås og log ud
            </button>
          </div>
        </div>

        {/* Quick Stats & Copy Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Stat Card */}
          <div className="bg-forest-900 text-white rounded-3xl p-6 shadow-xl border border-forest-800 flex flex-col justify-between">
            <div>
              <div className="w-12 h-12 bg-forest-800/80 rounded-2xl flex items-center justify-center text-forest-200 mb-4">
                <Users className="w-6 h-6" />
              </div>
              <p className="text-xs uppercase tracking-wider text-forest-300 font-semibold">
                Totalt tilmeldte
              </p>
              <h2 className="font-display font-bold text-4xl text-white mt-1">
                {subscribers.length} <span className="text-lg font-normal text-forest-300">modtagere</span>
              </h2>
            </div>
            <p className="text-xs text-forest-400 mt-6 pt-4 border-t border-forest-800">
              Listen synkroniseres automatisk, når nye beboere tilmelder sig på hjemmesiden.
            </p>
          </div>

          {/* Export & Copy Tools */}
          <div className="lg:col-span-2 bg-white rounded-3xl p-6 sm:p-8 shadow-sm border border-forest-100 space-y-4">
            <div className="flex items-center gap-2 text-forest-900 font-display font-bold text-lg">
              <Copy className="w-5 h-5 text-forest-600" />
              <span>Hurtig-kopiering af modtagerliste</span>
            </div>
            <p className="text-xs text-forest-600">
              Vælg det format, der passer til dit e-mailprogram, og indsæt direkte i Bcc-feltet:
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
              {/* Comma Copy */}
              <button
                onClick={() => copyToClipboard(getCommaList(), 'comma')}
                className={`p-4 rounded-2xl border text-left transition-all flex items-center justify-between cursor-pointer ${
                  copiedFormat === 'comma' 
                    ? 'bg-emerald-50 border-emerald-300 text-emerald-900 shadow-sm' 
                    : 'bg-forest-50/50 hover:bg-forest-50 border-forest-100 text-forest-900'
                }`}
              >
                <div>
                  <p className="text-xs font-bold flex items-center gap-1.5">
                    Kopiér m. komma (,)
                  </p>
                  <p className="text-[11px] text-forest-500 mt-0.5">Anbefalet til Gmail & Apple Mail</p>
                </div>
                {copiedFormat === 'comma' ? (
                  <span className="flex items-center gap-1 text-xs font-bold text-emerald-600">
                    <Check className="w-4 h-4" /> Kopieret!
                  </span>
                ) : (
                  <Copy className="w-4 h-4 text-forest-400" />
                )}
              </button>

              {/* Semicolon Copy */}
              <button
                onClick={() => copyToClipboard(getSemicolonList(), 'semicolon')}
                className={`p-4 rounded-2xl border text-left transition-all flex items-center justify-between cursor-pointer ${
                  copiedFormat === 'semicolon' 
                    ? 'bg-emerald-50 border-emerald-300 text-emerald-900 shadow-sm' 
                    : 'bg-forest-50/50 hover:bg-forest-50 border-forest-100 text-forest-900'
                }`}
              >
                <div>
                  <p className="text-xs font-bold flex items-center gap-1.5">
                    Kopiér m. semikolon (;)
                  </p>
                  <p className="text-[11px] text-forest-500 mt-0.5">Anbefalet til Outlook & Thunderbird</p>
                </div>
                {copiedFormat === 'semicolon' ? (
                  <span className="flex items-center gap-1 text-xs font-bold text-emerald-600">
                    <Check className="w-4 h-4" /> Kopieret!
                  </span>
                ) : (
                  <Copy className="w-4 h-4 text-forest-400" />
                )}
              </button>

              {/* Newline Copy */}
              <button
                onClick={() => copyToClipboard(getNewlineList(), 'line')}
                className={`p-4 rounded-2xl border text-left transition-all flex items-center justify-between cursor-pointer ${
                  copiedFormat === 'line' 
                    ? 'bg-emerald-50 border-emerald-300 text-emerald-900 shadow-sm' 
                    : 'bg-forest-50/50 hover:bg-forest-50 border-forest-100 text-forest-900'
                }`}
              >
                <div>
                  <p className="text-xs font-bold flex items-center gap-1.5">
                    Kopiér m. linjeskift
                  </p>
                  <p className="text-[11px] text-forest-500 mt-0.5">Ideel til indsætning i Excel/Word</p>
                </div>
                {copiedFormat === 'line' ? (
                  <span className="flex items-center gap-1 text-xs font-bold text-emerald-600">
                    <Check className="w-4 h-4" /> Kopieret!
                  </span>
                ) : (
                  <Copy className="w-4 h-4 text-forest-400" />
                )}
              </button>

              {/* CSV Download */}
              <button
                onClick={downloadCSV}
                className="p-4 rounded-2xl border border-forest-200 bg-forest-800 hover:bg-forest-900 text-white transition-all flex items-center justify-between shadow-sm cursor-pointer"
              >
                <div>
                  <p className="text-xs font-bold flex items-center gap-1.5">
                    Download CSV-fil
                  </p>
                  <p className="text-[11px] text-forest-200 mt-0.5">Hent som regnearksfil</p>
                </div>
                <Download className="w-4 h-4 text-forest-200" />
              </button>
            </div>
          </div>
        </div>

        {/* Manual Add & Search Filter */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Add Manual Subscriber */}
          <div className="bg-white rounded-3xl p-6 shadow-sm border border-forest-100 space-y-4">
            <h3 className="font-display font-bold text-base text-forest-950 flex items-center gap-2">
              <UserPlus className="w-4 h-4 text-forest-600" />
              Tilføj modtager manuelt
            </h3>
            <p className="text-xs text-forest-600">
              Brug denne formular til manuelt at tilføje en beboer til modtagerlisten.
            </p>

            <form onSubmit={handleAddManual} className="space-y-3">
              <div>
                <input
                  type="text"
                  placeholder="Navn (valgfrit)"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-xl bg-forest-50/50 border border-forest-200 text-xs text-forest-900 placeholder-forest-400 focus:outline-none focus:ring-2 focus:ring-forest-600"
                />
              </div>
              <div>
                <input
                  type="email"
                  required
                  placeholder="E-mailadresse *"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-xl bg-forest-50/50 border border-forest-200 text-xs text-forest-900 placeholder-forest-400 focus:outline-none focus:ring-2 focus:ring-forest-600"
                />
              </div>

              {addMessage && (
                <div className={`p-3 rounded-xl text-xs font-medium ${
                  addMessage.type === 'success' 
                    ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' 
                    : 'bg-rose-50 text-rose-800 border border-rose-200'
                }`}>
                  {addMessage.text}
                </div>
              )}

              <button
                type="submit"
                className="w-full py-2.5 px-4 bg-forest-800 hover:bg-forest-900 text-white font-semibold text-xs rounded-xl transition-all cursor-pointer"
              >
                + Tilføj til listen
              </button>
            </form>
          </div>

          {/* Subscribers Table & Search */}
          <div className="lg:col-span-2 bg-white rounded-3xl p-6 sm:p-8 shadow-sm border border-forest-100 space-y-6">
            
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h3 className="font-display font-bold text-lg text-forest-950">
                  Oversigt over modtagere
                </h3>
                <p className="text-xs text-forest-600">
                  Viser {filteredSubscribers.length} af {subscribers.length} tilmeldte e-mailadresser.
                </p>
              </div>

              {/* Search Bar */}
              <div className="relative max-w-xs w-full">
                <input
                  type="text"
                  placeholder="Søg på navn eller e-mail..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-3.5 py-2 rounded-xl bg-forest-50/50 border border-forest-200 text-xs text-forest-900 placeholder-forest-400 focus:outline-none focus:ring-2 focus:ring-forest-600"
                />
                <Search className="w-4 h-4 text-forest-400 absolute left-3 top-2.5" />
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto rounded-2xl border border-forest-100">
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="bg-forest-50/80 border-b border-forest-100 text-forest-800 font-semibold uppercase tracking-wider">
                    <th className="py-3 px-4">Navn</th>
                    <th className="py-3 px-4">E-mailadresse</th>
                    <th className="py-3 px-4">Tilmeldt Dato</th>
                    <th className="py-3 px-4 text-right">Handling</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-forest-50">
                  {filteredSubscribers.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="py-8 text-center text-forest-500 text-xs">
                        {searchTerm ? "Ingen modtagere matchede din søgning." : "Ingen tilmeldte modtagere fundet."}
                      </td>
                    </tr>
                  ) : (
                    filteredSubscribers.map((sub) => (
                      <tr key={sub.id} className="hover:bg-forest-50/40 transition-colors">
                        <td className="py-3.5 px-4 font-semibold text-forest-900">
                          {sub.name}
                        </td>
                        <td className="py-3.5 px-4 font-mono text-forest-700">
                          {sub.email}
                        </td>
                        <td className="py-3.5 px-4 text-forest-500">
                          {sub.date}
                        </td>
                        <td className="py-3.5 px-4 text-right">
                          <button
                            onClick={() => handleDelete(sub.id)}
                            title="Fjern fra modtagerliste"
                            className="p-1.5 rounded-lg text-rose-500 hover:text-rose-700 hover:bg-rose-50 transition-colors cursor-pointer"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

          </div>

        </div>

      </div>
    </div>
  );
}
